import type { FC, PropsWithChildren } from 'react';
import { createContext, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/browser';
import { UserDetails } from 'src/api/deinverband-api';
import { enhancedApi } from 'src/api/enhanced-api';
import { useAuthContext, useTenantContext } from 'src/hooks/use-context';
import { useHookMemo } from 'src/hooks/use-hook-memo';
import { updateFeatureFlags } from 'src/libs/feature-flags';
import { mapUserDetailsToProfile } from 'src/mappings/user-details-to-profile';
import { Profile } from 'src/types/user';
import { formatFullName } from 'src/utils/format';

export interface MeContextType {
  profile?: Profile;
  remote?: UserDetails;
}

export const MeContext = createContext<MeContextType | undefined>(undefined);

/**
 * Provide the user's profile information to children
 *
 * NOTE: This component is deployed inside the main app component. This allows
 * updates on logout/when not logged in (i.e. updating Sentry). Therefore, it
 * needs to render children, even when not authenticated (meaning the profile is
 * not available). Children should use the AuthGuard if authentication is
 * required.
 */
export const MeProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  const { authenticated, signOut } = useAuthContext();
  const {
    style: { adminFeatures, userFeatures }
  } = useTenantContext();

  const query = enhancedApi.useGetApiUsersMeQuery(undefined, {
    skip: !authenticated
  });

  const profile = useMemo(
    () => {
      const profile = mapUserDetailsToProfile(query.data);

      // IMPORTANT: this memo has side effects:
      // update global feature flags before the profile changes,
      // to ensure rendering is done with the correct flags
      updateFeatureFlags(profile?.tenantAdmin ? adminFeatures : userFeatures);

      return profile;
    },
    // feature flags will not change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data]
  );

  // update the Sentry user information whenever possible
  // (this might execute multiple times during login)
  useEffect(() => {
    if (authenticated)
      Sentry.setUser({
        id: profile?.id,
        email: profile?.email,
        username: formatFullName(profile)
      });
    else Sentry.setUser(null);
  }, [authenticated, profile]);

  // log out if the backend fails to retrieve the profile
  useEffect(
    () => {
      if (query.error) {
        Sentry.captureException(query.error);
        signOut('profile-error');
      }
    },
    // execute only on error (sign out is stable)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.error]
  );

  const context = useHookMemo({
    profile,
    remote: query.data
  });

  return <MeContext.Provider value={context}>{children}</MeContext.Provider>;
};
